import { t as __t } from "i18next";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomPagination from '../../paginations';
import RecordsTableFilter from '../../table-filter';
import colors from '../../../utils/colors';
import './style.css';

const SmartTable = ({
	columns = [],
	data = {},
	actions = null,
	keyword = '',
	onLazyLoad,
	recordsTableFilter = true,
	customPagination = true,
	startYear = '',
	endYear = '',
	colCheck = '',
	statusCheck = '',
	selectable = false,
	statusColKey = '',
	rowKey = 'id',
	onConfirmSelected = null,
	onSelectionChange = null,
	onSelectionSummaryChange = null,
	confirmButtonLabel = 'confirm',
	isRowSelectable = null,
	resetSelectedKey = 0,
	showConfirmButton = true,
	summaryColumns = [],
	externalSelectedIds = null,
	onLoadSelectAll,
	showSumRecord = true,
	initialPage = 1,
	initialMaxResult = 10,
	showRowNumber = true,
	rowNumberOffset = 0,
	tableRef = null,
	rowClassName = null,
	footer = null,
}) => {
	const { t } = useTranslation();
	const rootRef = useRef(null);
	const tableOuterRef = useRef(null);
	const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
	const [isScrolledToRight, setIsScrolledToRight] = useState(true);
	const [desktopWidth, setDesktopWidth] = useState(null);

	const [request, setRequest] = useState({
		page: initialPage || 1,
		maxResult: initialMaxResult || 10,
		keyword,
	});

	const [selectedIds, setSelectedIds] = useState([]);

	const resultData = useMemo(() => {
		return Array.isArray(data?.result) ? data.result : [];
	}, [data]);

	useEffect(() => {
		setRequest((prev) => ({ ...prev, keyword }));
	}, [keyword]);

	useLayoutEffect(() => {
		const root = rootRef.current;
		if (!root) return undefined;

		let rafId = null;
		let settleRaf = null;
		let settleFrames = 0;
		const timers = [];

		const measure = () => {
			const rect = root.getBoundingClientRect();
			const viewportWidth = document.documentElement.clientWidth || window.innerWidth || 0;
			const safeLeft = Math.max(0, rect.left);
			const viewportAvailable = Math.max(280, Math.floor(viewportWidth - safeLeft - 12));

			const parent = root.parentElement;
			const parentRect = parent?.getBoundingClientRect();
			const parentAvailable = parentRect
				? Math.max(0, Math.floor(Math.min(parentRect.right, viewportWidth - 12) - Math.max(parentRect.left, safeLeft)))
				: viewportAvailable;

			const nextWidth = Math.max(280, Math.min(viewportAvailable, parentAvailable || viewportAvailable));
			setDesktopWidth((prev) => (Math.abs((prev || 0) - nextWidth) > 1 ? nextWidth : prev));
		};

		const scheduleMeasure = () => {
			if (rafId) cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(measure);
		};

		// During first render the dashboard/sidebar can move the content without firing
		// a window resize. Watch several frames so the initial position always settles.
		const settleLayout = () => {
			measure();
			settleFrames += 1;
			if (settleFrames < 90) settleRaf = requestAnimationFrame(settleLayout);
		};
		settleRaf = requestAnimationFrame(settleLayout);

		const resizeObserver = typeof ResizeObserver !== 'undefined'
			? new ResizeObserver(scheduleMeasure)
			: null;

		let ancestor = root.parentElement;
		while (ancestor && ancestor !== document.body) {
			resizeObserver?.observe(ancestor);
			ancestor = ancestor.parentElement;
		}
		resizeObserver?.observe(document.documentElement);

		const mutationObserver = typeof MutationObserver !== 'undefined'
			? new MutationObserver(scheduleMeasure)
			: null;
		mutationObserver?.observe(document.body, {
			attributes: true,
			childList: true,
			subtree: true,
			attributeFilter: ['class', 'style'],
		});

		window.addEventListener('resize', scheduleMeasure);
		window.addEventListener('orientationchange', scheduleMeasure);
		document.addEventListener('transitionend', scheduleMeasure, true);
		document.addEventListener('animationend', scheduleMeasure, true);

		[0, 50, 150, 300, 600, 1000, 1500].forEach((delay) => {
			timers.push(window.setTimeout(scheduleMeasure, delay));
		});

		return () => {
			if (rafId) cancelAnimationFrame(rafId);
			if (settleRaf) cancelAnimationFrame(settleRaf);
			timers.forEach((id) => window.clearTimeout(id));
			resizeObserver?.disconnect();
			mutationObserver?.disconnect();
			window.removeEventListener('resize', scheduleMeasure);
			window.removeEventListener('orientationchange', scheduleMeasure);
			document.removeEventListener('transitionend', scheduleMeasure, true);
			document.removeEventListener('animationend', scheduleMeasure, true);
		};
	}, []);

	useEffect(() => {
		const container = tableOuterRef.current;
		if (!container) return undefined;

		const handleScroll = () => {
			const overflow = container.scrollWidth > container.clientWidth + 1;
			const atRight = !overflow || Math.ceil(container.scrollLeft + container.clientWidth) >= container.scrollWidth - 1;

			setHasHorizontalOverflow(overflow);
			setIsScrolledToRight(atRight);
		};

		handleScroll();
		container.addEventListener('scroll', handleScroll, { passive: true });
		window.addEventListener('resize', handleScroll);

		return () => {
			container.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', handleScroll);
		};
	}, [resultData, columns, actions, startYear, endYear]);

	useEffect(() => {
		setSelectedIds([]);
	}, [resetSelectedKey]);

	useEffect(() => {
		if (Array.isArray(externalSelectedIds)) {
			setSelectedIds(externalSelectedIds);
		}
	}, [externalSelectedIds]);

	const generateMonthColumns = (fromYear, toYear) => {
		const months = [];
		if (fromYear && toYear && fromYear <= toYear) {
			for (let y = fromYear; y <= toYear; y += 1) {
				for (let m = 1; m <= 12; m += 1) {
					months.push({ month: String(m).padStart(2, '0'), year: y });
				}
			}
		}
		return months;
	};

	const getYearRange = (fromYear, toYear) => {
		const years = [];
		if (!fromYear || !toYear || fromYear > toYear) return years;
		for (let y = fromYear; y <= toYear; y += 1) years.push(y);
		return years;
	};

	const canSelectRow = (row) => {
		if (statusCheck && statusColKey && !String(statusCheck).includes(String(row?.[statusColKey]))) {
			return false;
		}
		if (typeof isRowSelectable === 'function') {
			return isRowSelectable(row);
		}
		return true;
	};

	const months = generateMonthColumns(Number(startYear), Number(endYear));
	const years = getYearRange(Number(startYear), Number(endYear));

	const currentPageIds = useMemo(() => (
		resultData
			.filter((row) => canSelectRow(row))
			.map((row) => row?.[rowKey])
			.filter((id) => id !== undefined && id !== null)
	), [resultData, rowKey, statusCheck, statusColKey, isRowSelectable]);

	const selectedRows = useMemo(() => {
		const rowMap = new Map(resultData.map((item) => [item?.[rowKey], item]));
		return selectedIds.map((id) => rowMap.get(id)).filter(Boolean);
	}, [selectedIds, resultData, rowKey]);

	const selectedSummary = useMemo(() => {
		if (!Array.isArray(summaryColumns) || summaryColumns.length === 0) return {};

		return selectedRows.reduce((acc, row) => {
			summaryColumns.forEach((col) => {
				acc[col] = Number(acc[col] || 0) + Number(row?.[col] || 0);
			});
			return acc;
		}, {});
	}, [selectedRows, summaryColumns]);

	useEffect(() => {
		if (typeof onSelectionChange === 'function') {
			onSelectionChange(selectedIds);
		}

		if (typeof onSelectionSummaryChange === 'function') {
			onSelectionSummaryChange({
				ids: selectedIds,
				rows: selectedRows,
				summary: selectedSummary,
			});
		}
	}, [selectedIds, selectedRows, selectedSummary, onSelectionChange, onSelectionSummaryChange]);

	const isAllSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.includes(id));
	const isIndeterminate = currentPageIds.some((id) => selectedIds.includes(id)) && !isAllSelected;

	const handleSelectAll = async (checked) => {
		if (checked) {
			if (typeof onLoadSelectAll === 'function' && Number(data?.totalRecords || 0) > request.maxResult) {
				const result = await onLoadSelectAll({
					request,
					keyword,
					currentPageIds,
					currentRows: resultData,
				});

				if (Array.isArray(result?.ids)) {
					setSelectedIds(result.ids);
				}
				return;
			}

			setSelectedIds((prev) => Array.from(new Set([...prev, ...currentPageIds])));
			return;
		}

		setSelectedIds([]);
	};

	const handleSelectRow = (id, row, checked) => {
		if (id === undefined || id === null) return;
		if (checked) {
			setSelectedIds((prev) => Array.from(new Set([...prev, id])));
		} else {
			setSelectedIds((prev) => prev.filter((item) => item !== id));
		}
	};

	const handleConfirmSelected = () => {
		if (typeof onConfirmSelected === 'function') {
			onConfirmSelected({
				ids: selectedIds,
				rows: selectedRows,
				summary: selectedSummary,
			});
		}
	};

	const clearSelected = () => setSelectedIds([]);

	const getColumnLabel = (col) => {
		if (col?.header !== undefined) {
			return typeof col.header === 'function' ? col.header() : col.header;
		}
		if (col?.labelKey) return t(col.labelKey);
		if (!col?.label) return col?.key || '-';

		const tableKey = `table.col.${col.label}`;
		const tableTranslated = t(tableKey);
		if (tableTranslated && tableTranslated !== tableKey) return tableTranslated;

		const directTranslated = t(col.label);
		if (directTranslated && directTranslated !== col.label) return directTranslated;

		return col.label;
	};

	const renderCell = (col, row, idx) => {
		return col.render
			? col.render(row?.[col.key], row, { index: idx, displayIndex: getDisplayIndex(idx), request })
			: (row?.[col.key] ?? '-');
	};

	const getDisplayIndex = (idx) => {
		const explicitOffset = Number(rowNumberOffset);
		if (Number.isFinite(explicitOffset) && explicitOffset > 0) return explicitOffset + idx + 1;
		const page = Math.max(Number(request.page) || 1, 1);
		const maxResult = Math.max(Number(request.maxResult) || 10, 1);
		return ((page - 1) * maxResult) + idx + 1;
	};

	const tableClassName = [
		'smart-table__scroll',
		hasHorizontalOverflow ? 'smart-table__scroll--overflow' : '',
		!isScrolledToRight ? 'smart-table__scroll--has-more' : '',
		actions ? 'smart-table__scroll--actions-sticky' : '',
	].filter(Boolean).join(' ');

	return (
		<div className="smart-table" ref={rootRef} data-smart-table-version="2.6">
			{recordsTableFilter && (
				<div className="smart-table__toolbar">
					<RecordsTableFilter
						showConfirmButton={showConfirmButton}
						selectable={selectable}
						selectedIds={selectedIds}
						handleConfirmSelected={handleConfirmSelected}
						clearSelected={clearSelected}
						confirmButtonLabel={confirmButtonLabel}
						totalRecord={data?.totalRecords || 0}
						perPage={request.maxResult}
						sumRecord={showSumRecord ? (data?.sumRecords || []) : []}
						onChange={(e) => {
							const value = Number(e.target.value);
							setRequest((prev) => ({ ...prev, maxResult: value, page: 1 }));
							if (typeof onLazyLoad === 'function') {
								onLazyLoad({ maxResult: value, page: 1, keyword });
							}
						}}
					/>
				</div>
			)}

			{selectable && resultData.length > 0 && (
				<div className="smart-table__mobile-select-all">
					<label className="smart-table__check-label">
						<input
							type="checkbox"
							className="smart-table__checkbox"
							checked={isAllSelected}
							ref={(el) => {
								if (el) el.indeterminate = isIndeterminate;
							}}
							onChange={(e) => handleSelectAll(e.target.checked)}
							disabled={currentPageIds.length === 0}
						/>
						<span>{t('selected') || 'Selected'}: {selectedIds.length}</span>
					</label>
				</div>
			)}

			<div className="smart-table__desktop" style={desktopWidth ? { width: `${desktopWidth}px`, maxWidth: `${desktopWidth}px` } : undefined}>
				<div className={`smart-table__desktop-shell ${actions ? 'smart-table__desktop-shell--has-actions' : ''}`}>
					<div className={tableClassName} ref={tableOuterRef}>
						<table className="smart-table__table" ref={tableRef}>
							<thead>
								<tr>
									{selectable && (
										<th rowSpan="2" className="smart-table__select-col">
											<input
												type="checkbox"
												className="smart-table__checkbox"
												checked={isAllSelected}
												ref={(el) => {
													if (el) el.indeterminate = isIndeterminate;
												}}
												onChange={(e) => handleSelectAll(e.target.checked)}
												disabled={currentPageIds.length === 0}
												aria-label={t("accessibility.selectAllRows")}
											/>
										</th>
									)}

									{showRowNumber && <th rowSpan="2" className="smart-table__number-col">{t('no')}</th>}

									{columns.map((col, index) => (
										<th
											rowSpan="2"
											key={`${col.key || col.label || 'col'}-${index}`}
											style={col.style}
											className={col.headerClassName || col.className || ''}
										>
											{getColumnLabel(col)}
										</th>
									))}

									{startYear && endYear && years.map((itemYear) => (
										<th key={itemYear} colSpan={12} className="smart-table__year-head">
											{__t("uiText.text004")}{itemYear}
										</th>
									))}

									{actions && (
										<th rowSpan="2" className="smart-table__action-head">{t('action')}</th>
									)}
								</tr>

								<tr>
									{startYear && endYear && months.map((m) => (
										<th key={`${m.month}/${m.year}`} className="smart-table__month-head">
											{m.month}
										</th>
									))}
								</tr>
							</thead>

							<tbody>
								{resultData.length > 0 ? (
									resultData.map((row, idx) => {
										const id = row?.[rowKey];
										const checked = selectedIds.includes(id);
										const rowSelectable = canSelectRow(row);

										return (
											<tr
												key={id ?? idx}
												className={[
													checked ? 'smart-table__row--selected' : '',
													typeof rowClassName === 'function' ? rowClassName(row, idx) : ''
												].filter(Boolean).join(' ')}
											>
												{selectable && (
													<td className="smart-table__select-col">
														<input
															type="checkbox"
															className="smart-table__checkbox"
															checked={checked}
															disabled={!rowSelectable}
															onChange={(e) => handleSelectRow(id, row, e.target.checked)}
															aria-label={`Select row ${getDisplayIndex(idx)}`}
														/>
													</td>
												)}

												{showRowNumber && <td className="smart-table__number-col">{getDisplayIndex(idx)}</td>}

												{columns.map((col, colIdx) => (
													<td key={`${col.key || col.label || 'cell'}-${colIdx}`} className={col.className || ''}>
														{renderCell(col, row, idx)}
													</td>
												))}

												{startYear && endYear && months.map((m) => (
													<td key={`${m.month}/${m.year}`} className="smart-table__month-cell">
														{row?.[colCheck]?.includes(`${m.month}/${m.year}`) ? (
															<span className="smart-table__paid-check" style={{ color: colors.primary }}>✓</span>
														) : (
															<span className="smart-table__empty-mark">—</span>
														)}
													</td>
												))}

												{actions && (
													<td className="smart-table__action-cell">
														<div className="smart-table__actions-inner">{actions(row, idx, { displayIndex: getDisplayIndex(idx), request })}</div>
													</td>
												)}
											</tr>
										);
									})
								) : (
									<tr>
										<td
											colSpan={columns.length + (selectable ? 1 : 0) + (showRowNumber ? 1 : 0) + (startYear && endYear ? months.length : 0) + (actions ? 1 : 0)}
										>
											<div className="smart-table__empty-state">
												<div className="smart-table__empty-icon">—</div>
												<div className="smart-table__empty-title">{t('nodata')}</div>
											</div>
										</td>
									</tr>
								)}
							</tbody>
						{footer && <tfoot>{typeof footer === 'function' ? footer({ columns, data, request }) : footer}</tfoot>}
					</table>
					</div>

				</div>
			</div>

			<div className="smart-table__mobile">
				{resultData.length > 0 ? (
					resultData.map((row, idx) => {
						const id = row?.[rowKey];
						const checked = selectedIds.includes(id);
						const rowSelectable = canSelectRow(row);
						const primaryColumn = columns[0];
						const detailColumns = primaryColumn ? columns.slice(1) : columns;
						const paidMonths = startYear && endYear && Array.isArray(row?.[colCheck])
							? row[colCheck]
							: [];

						return (
							<article className={`smart-table-card ${checked ? 'smart-table-card--selected' : ''}`} key={id ?? idx}>
								<header className="smart-table-card__header">
									<div className="smart-table-card__heading">
										{showRowNumber && <span className="smart-table-card__index">#{getDisplayIndex(idx)}</span>}
										{primaryColumn && (
											<div className="smart-table-card__primary">
												<span className="smart-table-card__primary-label">{getColumnLabel(primaryColumn)}</span>
												<div className="smart-table-card__primary-value">{renderCell(primaryColumn, row)}</div>
											</div>
										)}
									</div>

									{selectable && (
										<input
											type="checkbox"
											className="smart-table__checkbox"
											checked={checked}
											disabled={!rowSelectable}
											onChange={(e) => handleSelectRow(id, row, e.target.checked)}
											aria-label={`Select row ${getDisplayIndex(idx)}`}
										/>
									)}
								</header>

								<div className="smart-table-card__body">
									{detailColumns.map((col, colIdx) => (
										<div className="smart-table-card__field" key={`${col.key || col.label || 'mobile'}-${colIdx}`}>
											<div className="smart-table-card__label">{getColumnLabel(col)}</div>
											<div className="smart-table-card__value">{renderCell(col, row, idx)}</div>
										</div>
									))}
								</div>

								{startYear && endYear && (
									<div className="smart-table-card__months">
										<div className="smart-table-card__label">{__t("uiText.text005")}</div>
										<div className="smart-table-card__month-list">
											{paidMonths.length > 0 ? paidMonths.map((month) => (
												<span className="smart-table-card__month-chip" key={month}>✓ {month}</span>
											)) : <span className="smart-table-card__muted">—</span>}
										</div>
									</div>
								)}

								{actions && (
									<footer className="smart-table-card__footer">
										<div className="smart-table-card__action-label">{t('action')}</div>
										<div className="smart-table__actions-inner">{actions(row, idx, { displayIndex: getDisplayIndex(idx), request })}</div>
									</footer>
								)}
							</article>
						);
					})
				) : (
					<div className="smart-table__empty-state smart-table__empty-state--mobile">
						<div className="smart-table__empty-icon">—</div>
						<div className="smart-table__empty-title">{t('nodata')}</div>
					</div>
				)}
			</div>

			{customPagination && Number(data?.totalPages || 0) > 0 && (
				<div className="smart-table__pagination">
					<CustomPagination
						currentPage={request.page}
						totalPages={Number(data?.totalPages || 0)}
						onPageChange={(page) => {
							if (page === request.page) return;
							if (page < 1 || page > Number(data?.totalPages || 0)) return;

							setRequest((prev) => ({ ...prev, page }));
							if (typeof onLazyLoad === 'function') {
								onLazyLoad({ maxResult: request.maxResult, page, keyword });
							}
						}}
						maxVisiblePages={5}
					/>
				</div>
			)}
		</div>
	);
};

export default SmartTable;
